import { useSyncExternalStore } from 'react';
import type { CivilizationResources } from '@galaxis/shared';
import { isFresher, makeBase, previewSpend, projectResources, type LiveResources, type ResourceBase } from './live-resources.ts';

/**
 * The one source of the displayed stockpile (header bar, economy, research, system panel, …).
 * Holds the last server snapshot and ticks the interpolation once a second; subscribers only re-render
 * when a displayed (whole-number / flag / minute-rounded ETA) value actually changes, never for the whole app.
 */

const TICK_MS = 1000;
const ETA_KEYS: readonly string[] = ['metalFullInH', 'energyFullInH', 'metalEmptyInH', 'energyEmptyInH'];

let base: ResourceBase | null = null;
let snapshot: LiveResources | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let syncRequested = false;
const listeners = new Set<() => void>();
let needSync: (() => void) | null = null;

function sameDisplay(a: LiveResources | null, b: LiveResources | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  for (const k of Object.keys(a) as Array<keyof LiveResources>) {
    if (ETA_KEYS.includes(k)) {
      const x = a[k] as number | null;
      const y = b[k] as number | null;
      if ((x === null) !== (y === null) || (x !== null && y !== null && Math.round(x * 60) !== Math.round(y * 60))) return false;
    } else if (a[k] !== b[k]) return false;
  }
  return true;
}

function recompute(now = Date.now()): void {
  const next = base ? projectResources(base, now) : null;
  // A stock that just ran dry changes the rates (energy exhaustion cuts mining): ask the server once
  if (next && base && !syncRequested && ((next.metalEmpty && base.res.metal > 0) || (next.energyEmpty && base.res.energy > 0))) {
    syncRequested = true;
    needSync?.();
  }
  if (sameDisplay(snapshot, next)) return;
  snapshot = next;
  listeners.forEach(l => l());
}

function startTicker(): void {
  if (!timer) timer = setInterval(() => recompute(), TICK_MS);
}

function stopTicker(): void {
  if (timer && listeners.size === 0) { clearInterval(timer); timer = null; }
}

/** Takes a server snapshot. Returns false (and changes nothing) when it is older than the one already held. */
export function ingestResources(res: CivilizationResources | null, receivedAt = Date.now(), rttMs = 0): boolean {
  if (!res) { base = null; syncRequested = false; recompute(); return true; }
  if (!isFresher(base, res)) return false;
  base = makeBase(res, receivedAt, rttMs);
  syncRequested = false;
  recompute(receivedAt);
  return true;
}

/** Shows the cost of an action right away; the next server snapshot replaces it. */
export function previewResourceSpend(metal: number, energy = 0, rp = 0): void {
  if (!base) return;
  base = previewSpend(base, Date.now(), metal, energy, rp);
  recompute();
}

export function getLiveResources(): LiveResources | null {
  recompute();
  return snapshot;
}

/** The sync module registers how to ask the server for a fresh snapshot. */
export function setSyncRequester(fn: (() => void) | null): void { needSync = fn; }

/** Forces a recompute now (tab back in focus, after a long sleep). */
export function refreshLiveResources(): void { recompute(); }

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  startTicker();
  recompute();
  return () => { listeners.delete(cb); stopTicker(); };
}

export function useLiveResources(): LiveResources | null {
  return useSyncExternalStore(subscribe, () => snapshot, () => null);
}
